import type { Quote_ } from 'src/api/db/apiQuote'
import type { InvoiceData, InvoiceOrgSeed, InvoiceParty } from './types'

/** Klayder AB — seller on Swedish invoices. */
export const DEFAULT_SELLER: InvoiceParty = {
  name: 'Klayder AB',
  addressLine1: 'Svandammsvägen 36',
  addressLine2: '',
  postal: '126 34',
  city: 'Hägersten',
  country: 'Sweden',
  orgNumber: '559433-8104',
  vatNumber: 'SE559433810401',
  email: 'carmelo@klayder.com',
}

const DEFAULT_PAYMENT = {
  paymentReference: '',
  paymentTermsDays: 30,
  bankgiro: '',
  plusgiro: '',
  iban: '',
  bic: '',
  currency: 'SEK',
  vatRate: 25,
} as const

export type BillingDocumentData = InvoiceData & {
  /** True when showing seeded defaults that are not yet saved on the quote. */
  isDraftPreview: boolean
}

export const formatDocumentDate = (value?: string | null): string => {
  if (!value) return '—'
  // Prefer date-only YYYY-MM-DD without timezone shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    if (Number.isNaN(date.getTime())) return '—'
    return new Intl.DateTimeFormat('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date)
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

export const toDateInputValue = (value?: string | null): string => {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export const addDaysDateOnly = (
  value: string | null | undefined,
  days: number
): string => {
  const base =
    toDateInputValue(value) || toDateInputValue(new Date().toISOString())
  const [y, m, d] = base.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  date.setDate(date.getDate() + days)
  return toDateInputValue(date.toISOString())
}

/** @deprecated use addDaysDateOnly + formatDocumentDate */
export const addDaysIso = (
  value: string | null | undefined,
  days: number
): string => addDaysDateOnly(value, days)

export const addDays = (
  value: string | null | undefined,
  days: number
): string => formatDocumentDate(addDaysDateOnly(value, days))

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback

const asNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback

const asBoolean = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback

const emptyParty = (): InvoiceParty => ({
  name: '',
  addressLine1: '',
  addressLine2: '',
  postal: '',
  city: '',
  country: '',
  orgNumber: '',
  vatNumber: '',
  email: '',
})

/** Fill blank party fields from fallback (empty string counts as blank). */
export const mergeParty = (
  primary: InvoiceParty,
  fallback: InvoiceParty
): InvoiceParty => ({
  name: primary.name.trim() || fallback.name,
  addressLine1: primary.addressLine1.trim() || fallback.addressLine1,
  addressLine2: primary.addressLine2.trim() || fallback.addressLine2,
  postal: primary.postal.trim() || fallback.postal,
  city: primary.city.trim() || fallback.city,
  country: primary.country.trim() || fallback.country,
  orgNumber: primary.orgNumber.trim() || fallback.orgNumber,
  vatNumber: primary.vatNumber.trim() || fallback.vatNumber,
  email: primary.email.trim() || fallback.email,
})

/** Old seed data before Klayder AB details were wired in. */
const isLegacyPlaceholderSeller = (party: InvoiceParty): boolean =>
  !party.name.trim() ||
  party.orgNumber === '556000-0000' ||
  party.addressLine1 === 'Storgatan 1'

const resolveSeller = (party: InvoiceParty): InvoiceParty =>
  isLegacyPlaceholderSeller(party)
    ? { ...DEFAULT_SELLER }
    : mergeParty(party, DEFAULT_SELLER)

const parseParty = (
  raw: unknown,
  fallback: InvoiceParty = emptyParty()
): InvoiceParty => {
  if (!isRecord(raw)) return { ...fallback }

  // Legacy issuer shape: address + postal (combined city/postal)
  const legacyAddress = asString(raw.address)
  const legacyPostal = asString(raw.postal)

  return mergeParty(
    {
      name: asString(raw.name),
      addressLine1: asString(raw.addressLine1, legacyAddress),
      addressLine2: asString(raw.addressLine2),
      postal: asString(raw.postal, legacyPostal),
      city: asString(raw.city),
      country: asString(raw.country),
      orgNumber: asString(raw.orgNumber),
      vatNumber: asString(raw.vatNumber),
      email: asString(raw.email),
    },
    fallback
  )
}

export const formatPartyAddress = (party: InvoiceParty): string => {
  const lines = [
    party.addressLine1,
    party.addressLine2,
    [party.postal, party.city].filter(Boolean).join(' '),
    party.country,
  ].filter((line) => line.trim().length > 0)
  return lines.join('\n') || '—'
}

export const buildBuyerFromOrg = (
  org?: InvoiceOrgSeed | null
): InvoiceParty => ({
  name: org?.name?.trim() || '',
  addressLine1: org?.billing_address_line1?.trim() || '',
  addressLine2: org?.billing_address_line2?.trim() || '',
  postal: org?.billing_postcode?.trim() || '',
  city: org?.billing_city?.trim() || '',
  country: org?.billing_country?.trim() || 'Sweden',
  orgNumber: org?.org_number?.trim() || '',
  vatNumber: org?.vat_number?.trim() || '',
  email: org?.billing_email?.trim() || '',
})

const resolvePaymentTermsDays = (quote: Quote_, fallback: number): number => {
  if (
    typeof quote.payment_terms === 'number' &&
    Number.isFinite(quote.payment_terms)
  ) {
    return quote.payment_terms
  }
  return fallback
}

const resolvePaymentReference = (quote: Quote_, fallback: string): string => {
  if (typeof quote.payment_reference === 'string') {
    return quote.payment_reference
  }
  return fallback
}

/** Parse quote.invoice_data jsonb into a typed InvoiceData, or null if unset. */
export const parseInvoiceData = (raw: unknown): InvoiceData | null => {
  if (!isRecord(raw)) return null

  const invoiceNumber = asString(raw.invoiceNumber)
  if (!invoiceNumber) return null

  const paymentTermsDays = asNumber(
    raw.paymentTermsDays,
    DEFAULT_PAYMENT.paymentTermsDays
  )
  const issueDate = toDateInputValue(
    asString(raw.issueDate, new Date().toISOString())
  )
  const dueDate = toDateInputValue(
    asString(raw.dueDate, addDaysDateOnly(issueDate, paymentTermsDays))
  )
  const supplyDate = toDateInputValue(asString(raw.supplyDate, issueDate))

  const seller = parseParty(raw.seller ?? raw.issuer, DEFAULT_SELLER)

  // Legacy: billToName only
  const buyerFallback = emptyParty()
  if (typeof raw.billToName === 'string' && raw.billToName) {
    buyerFallback.name = raw.billToName
  }
  const buyer = parseParty(raw.buyer, buyerFallback)

  return {
    invoiceNumber,
    issueDate,
    supplyDate,
    dueDate,
    paymentTermsDays,
    paymentReference: asString(
      raw.paymentReference,
      DEFAULT_PAYMENT.paymentReference
    ),
    bankgiro: asString(raw.bankgiro, DEFAULT_PAYMENT.bankgiro),
    plusgiro: asString(raw.plusgiro, DEFAULT_PAYMENT.plusgiro),
    iban: asString(raw.iban, DEFAULT_PAYMENT.iban),
    bic: asString(raw.bic, DEFAULT_PAYMENT.bic),
    currency: asString(raw.currency, DEFAULT_PAYMENT.currency),
    vatRate: asNumber(raw.vatRate, DEFAULT_PAYMENT.vatRate),
    fTaxApproved: asBoolean(raw.fTaxApproved, true),
    reverseCharge: asBoolean(raw.reverseCharge, false),
    ourReference: asString(raw.ourReference),
    yourReference: asString(raw.yourReference),
    notes: asString(raw.notes),
    seller,
    buyer,
    status:
      raw.status === 'draft' ||
      raw.status === 'issued' ||
      raw.status === 'sent' ||
      raw.status === 'paid'
        ? raw.status
        : undefined,
    createdAt: asString(raw.createdAt) || undefined,
    updatedAt: asString(raw.updatedAt) || undefined,
  }
}

/** Seed a new invoice document from quote + org billing defaults. */
export const buildDefaultInvoiceData = (quote: Quote_): InvoiceData => {
  const today = toDateInputValue(new Date().toISOString())
  const org = quote.organization as InvoiceOrgSeed | undefined
  const paymentTermsDays = resolvePaymentTermsDays(
    quote,
    DEFAULT_PAYMENT.paymentTermsDays
  )
  const paymentReference = resolvePaymentReference(
    quote,
    org?.default_invoice_reference?.trim() || DEFAULT_PAYMENT.paymentReference
  )
  const now = new Date().toISOString()

  return {
    invoiceNumber: `INV-${quote.public_id ?? quote.id}`,
    issueDate: today,
    supplyDate: today,
    dueDate: addDaysDateOnly(today, paymentTermsDays),
    paymentTermsDays,
    paymentReference,
    bankgiro: DEFAULT_PAYMENT.bankgiro,
    plusgiro: DEFAULT_PAYMENT.plusgiro,
    iban: DEFAULT_PAYMENT.iban,
    bic: DEFAULT_PAYMENT.bic,
    currency: org?.currency?.trim() || DEFAULT_PAYMENT.currency,
    vatRate: DEFAULT_PAYMENT.vatRate,
    fTaxApproved: true,
    reverseCharge: false,
    ourReference: '',
    yourReference: '',
    notes: '',
    seller: { ...DEFAULT_SELLER },
    buyer: buildBuyerFromOrg(org),
    status: 'draft',
    createdAt: now,
    updatedAt: now,
  }
}

/** Resolved invoice fields for the billing document UI. */
export const getBillingDocumentData = (quote: Quote_): BillingDocumentData => {
  const org = quote.organization as InvoiceOrgSeed | undefined
  const orgBuyer = buildBuyerFromOrg(org)
  const saved = parseInvoiceData(quote.invoice_data)

  if (!saved) {
    return { ...buildDefaultInvoiceData(quote), isDraftPreview: true }
  }

  const paymentTermsDays = resolvePaymentTermsDays(quote, saved.paymentTermsDays)
  const paymentReference = resolvePaymentReference(
    quote,
    saved.paymentReference
  )

  return {
    ...saved,
    paymentTermsDays,
    paymentReference,
    seller: resolveSeller(saved.seller),
    buyer: mergeParty(saved.buyer, orgBuyer),
    isDraftPreview: false,
  }
}

/** Build the invoice payload when production starts / invoice is issued. */
export const buildIssuedInvoiceData = (quote: Quote_): InvoiceData => {
  const current = getBillingDocumentData(quote)
  const { isDraftPreview: _preview, ...fields } = current
  const now = new Date().toISOString()
  const org = quote.organization as InvoiceOrgSeed | undefined

  return {
    ...fields,
    buyer: buildBuyerFromOrg(org),
    seller: resolveSeller(fields.seller),
    paymentTermsDays: resolvePaymentTermsDays(quote, fields.paymentTermsDays),
    paymentReference: resolvePaymentReference(quote, fields.paymentReference),
    status: 'issued',
    createdAt: fields.createdAt ?? now,
    updatedAt: now,
  }
}

/** Keep invoice_data payment fields in sync with quote columns. */
export const withSyncedPaymentFields = (
  invoiceData: unknown,
  payment_terms: number | null | undefined,
  payment_reference: string | null | undefined
): InvoiceData | null => {
  const parsed = parseInvoiceData(invoiceData)
  if (!parsed) return null

  return {
    ...parsed,
    paymentTermsDays:
      typeof payment_terms === 'number' && Number.isFinite(payment_terms)
        ? payment_terms
        : parsed.paymentTermsDays,
    paymentReference:
      typeof payment_reference === 'string'
        ? payment_reference
        : parsed.paymentReference,
    dueDate:
      typeof payment_terms === 'number' && Number.isFinite(payment_terms)
        ? addDaysDateOnly(parsed.issueDate, payment_terms)
        : parsed.dueDate,
    seller: resolveSeller(parsed.seller),
    updatedAt: new Date().toISOString(),
  }
}

export const QUOTE_VALIDITY_DAYS = 30
